import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, 
  Globe, 
  Users, 
  DollarSign, 
  Activity,
  Server,
  Shield,
  FileText,
  Settings,
  Plus,
  Search,
  ExternalLink,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Wrench,
  Database,
  CreditCard,
  HelpCircle,
  MessageSquare,
  Lock,
  Unlock,
  Target,
  Ticket,
  Mail,
  UserCog,
  ShieldCheck,
  ShieldX,
  Crown,
  Eye,
  MailCheck,
  Heart,
  Trash2,
  Edit,
  EyeOff,
  Handshake,
  Sparkles
} from 'lucide-react';
import SupportInbox from '../components/SupportInbox';
import PublicSubmissionsPanel from '../components/PublicSubmissionsPanel';
import PartnerAdmin from '../components/PartnerAdmin';
import OperationsRoadmap from '../components/OperationsRoadmap';
import DevelopmentHistory from '../components/DevelopmentHistory';
import AdRevenueProjections from '../components/VBB/AdRevenueProjections';
import UserManagementPanel from '../components/VBB/panels/UserManagementPanel';
import PatronsManagementPanel from '../components/VBB/panels/PatronsManagementPanel';
import TicketsPanel from '../components/VBB/panels/TicketsPanel';
import AiFeaturesPanel from '../components/VBB/panels/AiFeaturesPanel';
import SeoPanel from '../components/VBB/panels/SeoPanel';
import InvestorDocsPanel from '../components/InvestorDocsPanel';
import { formatDistanceToNow } from 'date-fns';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { useSupportTicketPulse } from '../hooks/useSupportTicketPulse';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLUB_STATUS = {
  active: { label: 'Active', color: 'bg-green-500', icon: CheckCircle },
  pending: { label: 'Pending', color: 'bg-yellow-500', icon: Clock },
  suspended: { label: 'Suspended', color: 'bg-red-500', icon: AlertTriangle },
  trial: { label: 'Trial', color: 'bg-blue-500', icon: Clock },
};

const VBBOperationsCenter = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClub, setSelectedClub] = useState(null);
  const [clubDetailOpen, setClubDetailOpen] = useState(false);

  // Access control - VBB operators only
  const isVBBOperator = user?.is_operator_admin || user?.email?.endsWith('@verybigbox.com');

  // Polls active-ticket count every 30s + fires browser notifications on growth
  const { count: supportTicketCount } = useSupportTicketPulse({ enabled: !!isVBBOperator });

  useEffect(() => {
    // Wait for auth to finish loading before checking access
    if (authLoading) return;
    
    // If auth is done loading but user is null, they're not logged in
    if (!user) {
      toast.error('Please log in to access this area');
      navigate('/login');
      return;
    }
    
    if (!isVBBOperator) {
      toast.error('Access denied');
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVBBOperator, authLoading, user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Support both bearer-token auth and cookie-auth (auto-login). When the
      // admin lands via /auto-login, `token` is undefined — without
      // credentials:'include' the API 401s and every stat gets zeroed out.
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const reqOpts = { headers, credentials: 'include' };

      // Fetch platform-wide stats
      const [statsRes, clubsRes] = await Promise.all([
        fetch(`${API_URL}/api/vbb/stats`, reqOpts),
        fetch(`${API_URL}/api/vbb/clubs`, reqOpts)
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      } else {
        // Backend unavailable — show zeros rather than fake data so admins
        // aren't misled about real traction.
        setStats({
          total_clubs: 0,
          total_users: 0,
          total_members: 0,
          total_revenue_mtd: 0,
          total_revenue_ytd: 0,
          vbb_revenue_mtd: 0,
          vbb_revenue_ytd: 0,
          active_clubs: 0,
          pending_clubs: 0,
          support_tickets_open: 0,
          uptime_percent: null,
          api_calls_today: null,
          storage_used_gb: null,
          _unavailable: true,
        });
      }

      if (clubsRes.ok) {
        setClubs(await clubsRes.json());
      } else {
        setClubs([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredClubs = clubs.filter(club => 
    club.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    club.domain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show loading while auth is being checked or user data is being fetched
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // If not loading but no user, they need to log in (handled by useEffect redirect)
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white">Redirecting to login...</div>
      </div>
    );
  }

  if (!isVBBOperator) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">VBB Operations Center is only accessible to authorized VBB personnel.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800" data-testid="vbb-operations-center">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-900 border-b border-purple-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">World Pets Operations</h1>
                <p className="text-purple-200 text-sm">worldpets.club - Global Pet Club Platform</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-green-400 border-green-400">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />
                All Systems Operational
              </Badge>
              <Link to="/sysadmin">
                <Button variant="outline" size="sm" className="gap-2 text-white border-white/30 hover:bg-white/10">
                  <Settings className="w-4 h-4" />
                  Sysadmin
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          {[
            { label: 'Total Clubs', value: stats?.total_clubs || 0, icon: Building2, color: 'text-purple-400' },
            { label: 'Total Users', value: stats?.total_users?.toLocaleString() || 0, icon: Users, color: 'text-blue-400' },
            { label: 'Members', value: stats?.total_members?.toLocaleString() || 0, icon: Users, color: 'text-green-400' },
            { label: 'Revenue MTD', value: `$${(stats?.total_revenue_mtd || 0).toLocaleString()}`, icon: DollarSign, color: 'text-emerald-400' },
            { label: 'VBB Revenue MTD', value: `$${(stats?.vbb_revenue_mtd || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-amber-400' },
            { label: 'Uptime', value: `${stats?.uptime_percent || 99.9}%`, icon: Activity, color: 'text-cyan-400' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label} className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <Icon className={`w-5 h-5 ${color}`} />
                  <span className="text-xs text-slate-400">{label}</span>
                </div>
                <p className="text-2xl font-bold text-white mt-2">{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700 flex flex-wrap h-auto gap-1 justify-start" data-testid="ops-tabs-list">
            <TabsTrigger value="overview" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="clubs" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white">
              <Globe className="w-4 h-4 mr-2" />
              Clubs
            </TabsTrigger>
            <TabsTrigger value="infrastructure" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white">
              <Server className="w-4 h-4 mr-2" />
              Infrastructure
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="support" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white" data-testid="ops-tab-support">
              <HelpCircle className="w-4 h-4 mr-2" />
              Support
              {supportTicketCount > 0 && (
                <span
                  className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold leading-none"
                  data-testid="ops-support-badge"
                >
                  {supportTicketCount > 99 ? '99+' : supportTicketCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="roadmap" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white">
              <Target className="w-4 h-4 mr-2" />
              Roadmap
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="projections" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white">
              <TrendingUp className="w-4 h-4 mr-2" />
              Ad Revenue
            </TabsTrigger>
            <TabsTrigger value="tickets" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white">
              <Ticket className="w-4 h-4 mr-2" />
              Tickets
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white">
              <UserCog className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="patrons" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white">
              <Heart className="w-4 h-4 mr-2" />
              Patrons
            </TabsTrigger>
            <TabsTrigger value="partners-admin" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white">
              <Handshake className="w-4 h-4 mr-2" />
              Partners
            </TabsTrigger>
            <TabsTrigger value="ai-features" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white" data-testid="ops-ai-tab">
              <Sparkles className="w-4 h-4 mr-2" />
              AI
            </TabsTrigger>
            <TabsTrigger value="seo" className="data-[state=active]:bg-purple-600 text-slate-200 data-[state=active]:text-white hover:text-white" data-testid="ops-seo-tab">
              <Search className="w-4 h-4 mr-2" />
              SEO
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Revenue Chart Placeholder */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    Revenue Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48 flex items-center justify-center border border-dashed border-slate-600 rounded-lg">
                    <div className="text-center text-slate-400">
                      <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Revenue chart coming soon</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-xs text-slate-400">YTD Revenue</p>
                      <p className="text-xl font-bold text-white">${(stats?.total_revenue_ytd || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-xs text-slate-400">VBB Share (10%)</p>
                      <p className="text-xl font-bold text-emerald-400">${(stats?.vbb_revenue_ytd || 0).toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-purple-400" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-slate-600 hover:bg-slate-700 text-slate-100">
                    <Plus className="w-5 h-5" />
                    <span className="text-xs">New Club</span>
                  </Button>
                  <Link to="/sysadmin">
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-slate-600 hover:bg-slate-700 text-slate-100 w-full">
                      <Users className="w-5 h-5" />
                      <span className="text-xs">User Admin</span>
                    </Button>
                  </Link>
                  <Link to="/about/service-agreement">
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-slate-600 hover:bg-slate-700 text-slate-100 w-full">
                      <FileText className="w-5 h-5" />
                      <span className="text-xs">Agreements</span>
                    </Button>
                  </Link>
                  <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-slate-600 hover:bg-slate-700 text-slate-100">
                    <Shield className="w-5 h-5" />
                    <span className="text-xs">Security</span>
                  </Button>
                </CardContent>
              </Card>

              {/* Active Clubs */}
              <Card className="bg-slate-800/50 border-slate-700 md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-blue-400" />
                      Active Clubs
                    </span>
                    <Button size="sm" variant="outline" className="border-slate-600 text-slate-100 hover:bg-slate-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Club
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Club</TableHead>
                        <TableHead className="text-slate-400">Domain</TableHead>
                        <TableHead className="text-slate-400">Region</TableHead>
                        <TableHead className="text-slate-400">Members</TableHead>
                        <TableHead className="text-slate-400">Revenue MTD</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clubs.map(club => (
                        <TableRow key={club.id} className="border-slate-700">
                          <TableCell className="text-white font-medium">{club.name}</TableCell>
                          <TableCell>
                            <a href={`https://${club.domain}`} target="_blank" rel="noopener noreferrer" 
                               className="text-blue-400 hover:underline flex items-center gap-1">
                              {club.domain}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </TableCell>
                          <TableCell className="text-slate-300">{club.country}</TableCell>
                          <TableCell className="text-slate-300">{club.members?.toLocaleString()}</TableCell>
                          <TableCell className="text-emerald-400">${club.revenue_mtd?.toLocaleString()}</TableCell>
                          <TableCell>
                            <Badge className={`${CLUB_STATUS[club.status]?.color} text-white`}>
                              {CLUB_STATUS[club.status]?.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => {
                              setSelectedClub(club);
                              setClubDetailOpen(true);
                            }}>
                              <Settings className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Clubs Tab */}
          <TabsContent value="clubs" className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Club Management</CardTitle>
                  <div className="flex gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        placeholder="Search clubs..." 
                        className="pl-10 bg-slate-700 border-slate-600 text-white w-64"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Button className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Onboard New Club
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Club Name</TableHead>
                      <TableHead className="text-slate-400">Domain</TableHead>
                      <TableHead className="text-slate-400">Region</TableHead>
                      <TableHead className="text-slate-400">Members</TableHead>
                      <TableHead className="text-slate-400">Revenue MTD</TableHead>
                      <TableHead className="text-slate-400">VBB Fee</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Created</TableHead>
                      <TableHead className="text-slate-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClubs.map(club => (
                      <TableRow key={club.id} className="border-slate-700 hover:bg-slate-700/50">
                        <TableCell className="text-white font-medium">{club.name}</TableCell>
                        <TableCell>
                          <a href={`https://${club.domain}`} target="_blank" rel="noopener noreferrer" 
                             className="text-blue-400 hover:underline flex items-center gap-1">
                            {club.domain}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </TableCell>
                        <TableCell className="text-slate-300">{club.country}</TableCell>
                        <TableCell className="text-slate-300">{club.members?.toLocaleString()}</TableCell>
                        <TableCell className="text-emerald-400">${club.revenue_mtd?.toLocaleString()}</TableCell>
                        <TableCell className="text-amber-400">${((club.revenue_mtd || 0) * 0.1).toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge className={`${CLUB_STATUS[club.status]?.color} text-white`}>
                            {CLUB_STATUS[club.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-400 text-sm">
                          {new Date(club.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white">
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Infrastructure Tab */}
          <TabsContent value="infrastructure" className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Server className="w-5 h-5 text-cyan-400" />
                    Servers
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 bg-green-900/30 border border-green-700 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-green-400">Primary (US-East)</span>
                      <Badge className="bg-green-500">Healthy</Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">CPU: 24% | Memory: 61%</p>
                  </div>
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">CDN (Global)</span>
                      <Badge className="bg-green-500">Active</Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">45 edge locations</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Database className="w-5 h-5 text-purple-400" />
                    Database
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 bg-green-900/30 border border-green-700 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-green-400">MongoDB Atlas</span>
                      <Badge className="bg-green-500">Connected</Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">M10 Cluster | 3 Nodes</p>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Storage Used</span>
                    <span className="text-white">{stats?.storage_used_gb || 0} GB</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Connections</span>
                    <span className="text-white">23 / 500</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-400" />
                    API Activity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Requests Today</span>
                    <span className="text-white">{(stats?.api_calls_today || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Avg Response</span>
                    <span className="text-green-400">89ms</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Error Rate</span>
                    <span className="text-green-400">0.02%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Uptime (30d)</span>
                    <span className="text-green-400">{stats?.uptime_percent || 99.9}%</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Revenue & Billing</CardTitle>
                <CardDescription className="text-slate-400">
                  VBB receives 10% of gross revenues from all clubs, which includes payment processing fees.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <p className="text-xs text-slate-400">Total Revenue MTD</p>
                    <p className="text-2xl font-bold text-white">${(stats?.total_revenue_mtd || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-emerald-900/30 border border-emerald-700 rounded-lg">
                    <p className="text-xs text-emerald-400">VBB Share (10%)</p>
                    <p className="text-2xl font-bold text-emerald-400">${(stats?.vbb_revenue_mtd || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <p className="text-xs text-slate-400">Payment Processing</p>
                    <p className="text-2xl font-bold text-white">~${((stats?.total_revenue_mtd || 0) * 0.029).toFixed(0)}</p>
                  </div>
                  <div className="p-4 bg-amber-900/30 border border-amber-700 rounded-lg">
                    <p className="text-xs text-amber-400">Net to VBB</p>
                    <p className="text-2xl font-bold text-amber-400">
                      ${((stats?.vbb_revenue_mtd || 0) - ((stats?.total_revenue_mtd || 0) * 0.029)).toFixed(0)}
                    </p>
                  </div>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Club</TableHead>
                      <TableHead className="text-slate-400">Gross Revenue</TableHead>
                      <TableHead className="text-slate-400">VBB Fee (10%)</TableHead>
                      <TableHead className="text-slate-400">Payment Processing</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clubs.map(club => (
                      <TableRow key={club.id} className="border-slate-700">
                        <TableCell className="text-white">{club.name}</TableCell>
                        <TableCell className="text-white">${(club.revenue_mtd || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-emerald-400">${((club.revenue_mtd || 0) * 0.1).toFixed(2)}</TableCell>
                        <TableCell className="text-slate-400">${((club.revenue_mtd || 0) * 0.029).toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-500">Full</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-6">
            <SupportInbox darkMode={true} />
            <PublicSubmissionsPanel darkMode={true} />
          </TabsContent>

          {/* Roadmap Tab */}
          <TabsContent value="roadmap" className="space-y-6">
            <OperationsRoadmap />
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            {/* Development History Section */}
            <DevelopmentHistory />
            
            {/* Dynamic Investor / Operator Documents (YC prep, SAFE drafts, research, articles) */}
            <InvestorDocsPanel />

            {/* Printable NGO Handout Card (1/4-page, double-sided) */}
            <Card className="bg-slate-800/50 border-slate-700" data-testid="handout-downloads-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-400" />
                  Printable NGO Handout Card
                </CardTitle>
                <CardDescription className="text-slate-400">
                  1/4-page (4.25" × 5.5"), double-sided, Forest Pine + Terracotta brand.
                  Download the single-card PDF for pro print, or the 4-up US Letter sheet to run copies on a desktop printer. 
                  To regenerate (or add a new NGO slug) run:
                  <code className="ml-1 px-1.5 py-0.5 rounded bg-slate-900/70 text-amber-300 text-xs">
                    python /app/scripts/render_handout_card.py &lt;slug&gt;
                  </code>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { slug: 'grey-muzzle', name: 'The Grey Muzzle Organization' },
                    { slug: 'iheartdogs', name: 'iHeartDogs' },
                    { slug: 'zigzag', name: 'Zigzag' },
                    { slug: 'best-friends', name: 'Best Friends Animal Society' },
                    { slug: 'petfinder-foundation', name: 'Petfinder Foundation' },
                    { slug: 'good-origins', name: 'Good Origins' },
                  ].map(({ slug, name }) => (
                    <div
                      key={slug}
                      className="p-4 bg-slate-700/50 rounded-lg border border-slate-600"
                      data-testid={`handout-row-${slug}`}
                    >
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div>
                          <p className="font-medium text-white">{name}</p>
                          <p className="text-xs text-slate-400">slug: {slug}</p>
                        </div>
                        <Link
                          to={`/preview/handout/${slug}`}
                          target="_blank"
                          className="text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"
                        >
                          Preview <ExternalLink className="w-3 h-3" />
                        </Link>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <a
                          href={`/downloads/handout_card_${slug}.pdf`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-semibold transition"
                          data-testid={`handout-dl-single-${slug}`}
                        >
                          <FileText className="w-3.5 h-3.5" /> Single card (2-page, native size)
                        </a>
                        <a
                          href={`/downloads/handout_card_${slug}_4up_letter.pdf`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-600 hover:bg-slate-500 text-white text-xs font-semibold transition"
                          data-testid={`handout-dl-4up-${slug}`}
                        >
                          <FileText className="w-3.5 h-3.5" /> 4-up US Letter (print & cut)
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-4 italic">
                  Tip: send the 4-up Letter PDF to a standard home/office printer, then slice with a guillotine or trimmer along the dashed lines — 4 handouts per sheet.
                </p>
              </CardContent>
            </Card>

            {/* Legacy links to on-site pages */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Public Site Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <Link to="/about/service-agreement">
                    <div className="p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-purple-400" />
                        <div>
                          <p className="font-medium text-white">Service Agreement Template</p>
                          <p className="text-sm text-slate-400">Master agreement between VBB and clubs</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <Link to="/about/story">
                    <div className="p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-amber-400" />
                        <div>
                          <p className="font-medium text-white">Club Story</p>
                          <p className="text-sm text-slate-400">History and vision of the platform</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ad Revenue Projections Tab */}
          <TabsContent value="projections" className="space-y-6">
            <AdRevenueProjections />
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets" className="space-y-6">
            <TicketsPanel />
          </TabsContent>

          <TabsContent value="users" className="space-y-6">
            <UserManagementPanel />
          </TabsContent>

          <TabsContent value="patrons" className="space-y-6">
            <PatronsManagementPanel />
          </TabsContent>

          <TabsContent value="partners-admin" className="space-y-6">
            <PartnerAdmin darkMode={true} />
          </TabsContent>

          <TabsContent value="ai-features" className="space-y-6">
            <AiFeaturesPanel />
          </TabsContent>

          <TabsContent value="seo" className="space-y-6">
            <SeoPanel />
          </TabsContent>
        </Tabs>
      </div>

      {/* Club Detail Dialog */}
      <Dialog open={clubDetailOpen} onOpenChange={setClubDetailOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedClub?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Club management and configuration
            </DialogDescription>
          </DialogHeader>
          {selectedClub && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Domain</p>
                  <p className="text-white">{selectedClub.domain}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Status</p>
                  <Badge className={`${CLUB_STATUS[selectedClub.status]?.color} text-white`}>
                    {CLUB_STATUS[selectedClub.status]?.label}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Members</p>
                  <p className="text-white">{selectedClub.members?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Revenue MTD</p>
                  <p className="text-emerald-400">${selectedClub.revenue_mtd?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setClubDetailOpen(false)}>Close</Button>
            <Button className="bg-purple-600 hover:bg-purple-700">Edit Club</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VBBOperationsCenter;
